import { useState, useCallback, useRef, useEffect } from "react";
import {
  chatStream,
  approveChainStream,
  clearChat as apiClearChat,
  confirmWrite as apiConfirmWrite,
  cancelChat as apiCancelChat,
  endSession as apiEndSession,
  listSavedChats,
  getSavedChat,
  saveChat as apiSaveChat,
  deleteSavedChat,
  restoreChat as apiRestoreChat,
} from "@/api/client";
import { useSSE } from "./useSSE";
import { effectiveCost } from "@/utils/cost";
import type {
  ChatMessage,
  SSEEvent,
  UsageInfo,
  MemoryRecallItem,
  GoalState,
  DraftActionPlan,
  ChainPlanState,
  ChainPlanStepState,
} from "@/types";
import { EMPTY_GOAL_STATE } from "@/types";

// ── Pipeline node types for the dev panel ──────────
export type PipelineNode =
  | {
      kind: "intent";
      intents: string[];
      entities?: string[];
      display?: boolean;
      pruned?: number;
    }
  | {
      kind: "router";
      groups: string[];
      toolsLoaded: number;
      totalTools: number;
      cost?: number;
      enrichedQuery?: string;
      backrefs?: Record<string, string>;
      carried?: number;
      autoChain?: boolean;
    }
  | { kind: "thinking"; durationMs?: number; text?: string }
  | { kind: "user_message"; text: string; tsMs?: number }
  | {
      kind: "tool";
      tool: string;
      input: Record<string, unknown>;
      result?: string;
      durationMs?: number;
      isError: boolean;
      declined?: boolean;
    }
  | { kind: "planner"; text: string }
  | { kind: "active"; text: string }
  | {
      kind: "confirm";
      confirmId: string;
      tools: { tool: string; input: Record<string, unknown> }[];
      readTools?: string[];
      resolved?: "approved" | "rejected";
    }
  | { kind: "memory"; memory: Record<string, unknown> }
  | {
      kind: "memory_recall";
      memories: {
        layer: string;
        category?: string;
        key?: string;
        value?: string;
        confidence?: number;
        content?: string;
      }[];
      fullContext?: string;
      durationMs?: number;
    }
  | {
      kind: "pipeline_trace";
      engine?: string;
      intent?: Record<string, unknown>;
      fql?: Record<string, unknown>;
      capability?: Record<string, unknown>;
      final_requests?: Array<Record<string, unknown>>;
      fan_out_note?: string;
    }
  | { kind: "prewarm"; summary?: string; hints: Array<{ mention: string; candidates: Array<{ id: string | number; label: string; entity: string; hint: string; score?: number; tier?: string }>; }> }
  // ── New planner narration (Q3/Q4/A2) ───────────────────────────────
  // Plan card with mutating step checklist; same node carries the
  // live status. step_* events mutate the plan in-place rather than
  // appending new nodes, so the DevPanel renders one plan card per
  // chain attempt instead of N separate "step started" rows.
  | {
      kind: "plan";
      planId: string;
      totalSteps: number;
      steps: Array<{
        stepIdx: number;
        opId: string;
        summary?: string;
        opHumanLabel?: string;
        status: "pending" | "running" | "completed" | "failed" | "skipped";
        resultSummary?: string;
        newHandleId?: string | null;
        newEntityId?: number | string | null;
        durationMs?: number;
        failureMessage?: string;
      }>;
      trigger?: string;
      // ── Goal/Plan primitive (Phase C). When the planner emits
      // `plan_linked_to_goal` after this plan committed, `goal` gets
      // set and the plan card renders a "Plan for: …" header.
      // `outcome` is set by `plan_unlinked` ("completed" / "failed"
      // / "cancelled") so the card can show a terminal banner.
      goal?: string;
      outcome?: string;
    }
  | {
      kind: "chain_aborted";
      planId?: string | null;
      reason: string;
      completedSteps: number;
      partialHandles: Array<{
        step: number;
        opId: string;
        handleId?: string | null;
        newId?: number | string | null;
      }>;
    }
  | {
      kind: "plan_missing";
      trigger: string;
      firstOpId?: string | null;
      warning?: string;
    }
  | {
      kind: "prewarm_filter";
      status?: string;
      kept?: Record<string, string[]>;
      dropped?: Record<string, string[]>;
      confidence?: string;
    }
  | {
      // F13 — planner-side memory_filter. Lives next to prewarm_filter
      // in the devbar. Three input counts (always/scope/recall_in)
      // tell you what bucket each candidate landed in; recall_kept
      // is what survived the small-LLM filter on the noise bucket.
      // outboundDropped is the deterministic-drop bucket (recent_event
      // with direction=outbound — prior assistant responses).
      kind: "memory_filter";
      status?: string;
      confidence?: string;
      alwaysCount?: number;
      scopeCount?: number;
      recallIn?: number;
      recallKept?: number;
      outboundDropped?: number;
      durationMs?: number;
    }
  | {
      // Tier 5 (attribute) or Tier 6 (metric) hits — deterministic
      // prewarm derived from graph.json / nested_leaves.yaml at
      // turn start. Renders alongside the legacy Tier-1/Tier-3
      // panels in DevPanel.
      kind: "tier_hit";
      tier: string;
      hits: Record<string, Array<Record<string, unknown>>>;
    }
  | {
      // Memory Manager — the LLM fired save_fact mid-turn. Renders in
      // DevPanel as a small "remembered" row so the dev can see what
      // got persisted. `pending` discriminates pending_confirmation
      // saves (writer flagged for user confirmation) from green-light
      // saves.
      kind: "fact_saved";
      pending: boolean;
      factId?: string;
      factType?: string;
      humanReadable?: string;
      scope?: { type?: string; id?: string | number };
      status?: string;
    }
  | {
      // Owen 2026-05-26 — full LLM input snapshot per iteration.
      // Carries the system prompt, the messages array, and the list
      // of tool names the model has access to. DevPanel renders an
      // expandable block so the dev can audit exactly what context
      // the model saw before each call.
      kind: "llm_input";
      iteration: number;
      systemPrompt: string;
      messages: Array<{ role?: string; content?: unknown; [k: string]: unknown }>;
      toolNames: string[];
      systemPromptChars: number;
      messagesChars: number;
      tsMs?: number;
    }
  | {
      // v2 Part 6.b (2026-05-29) — surface classifier output in the
      // pipeline trace so the dev can confirm what the classifier said
      // per message. All fields are exactly what the backend sends on
      // the `llm_classifier` SSE event.
      kind: "classifier";
      source: string;
      model?: string;
      durationMs?: number;
      messageClass?: string;
      goalSignal?: string;
      topicIntent?: string | null;
      newGoalLabel?: string | null;
      confidence?: string;
      entities?: Array<Record<string, unknown>>;
      toResolve?: Array<Record<string, unknown>>;
      activeEntity?: Record<string, unknown> | null;
      directReplyPresent?: boolean;
      inputTokens?: number;
      outputTokens?: number;
      cachedTokens?: number;
      costUsd?: number;
      error?: string | null;
    }
  | { kind: "done"; usage?: UsageInfo; toolCount: number };

// ── Topic tracking Part 5 (Owen + bug-diagnoser 2026-05-28) ──────────
// Reads the planner's `topic_understanding` SSE event. Sticky — the
// most recent payload, not a stream. Renders in DevPanel's
// Conversation Understander block at the top of the panel.
// Spec: planner-manager/public/ui-dev-conversation-understander-block-2026-05-28.md
export type TopicActiveEntity = {
  type: "Job" | "Quote" | "Customer" | "Site" | "Invoice" | "RecurringJob";
  id: number | null;
  label: string | null;
  confidence: "high" | "med" | "low";
  source:
    | "explicit_in_message"
    | "coreference"
    | "carryover_from_prior_turn"
    | "type_only_from_intent";
};

export type TopicIntent =
  | "read"
  | "update"
  | "schedule_write"
  | "create_root"
  | "create_child"
  | "destroy"
  | "convert"
  | "none";

export interface TopicUnderstanding {
  goal: string | null;
  active_entity: TopicActiveEntity | null;
  topic_intent: TopicIntent | null;
  pinned_path: string | null;
  prewarm_actions: string[];
  history_pair_count: number;
}

// ── v2 Part 6 (Owen + bug-diagnoser 2026-05-28) ──
// Per-prefetch SSE payload the planner emits from prewarm_v2.
// Devbar PROACTIVELY LOADED panel renders one row per item.
export interface PrewarmActionItem {
  action: string;          // display_all | inspect_relationships | fk_load | find_paths | resolve
  target: string;
  duration_ms: number;
  ok: boolean;
  bytes: number;
  cache_hit: boolean;
  error: string | null;
}

// SESSION COST row state. `thisTurnUsd` resets to 0 on user_message;
// `totalUsd` accumulates for the whole chat session. `turnCount` is
// the number of user turns priced into the total.
export interface SessionCost {
  thisTurnUsd: number;
  totalUsd: number;
  turnCount: number;
}

export interface PendingConfirm {
  confirmId: string;
  tools: { tool: string; input: Record<string, unknown> }[];
  readTools?: string[];
  reasoning?: string;
}

export interface PipelineRun {
  id: number;
  nodes: PipelineNode[];
  systemPrompt?: string;
  /** ms since epoch when the user message that triggered this run was sent. */
  startedAt?: number;
  /** ms since epoch when the final `done` event arrived. */
  endedAt?: number;
}

export interface HttpTraceItem {
  id: number;             // monotonic, for React key
  opId?: string | null;
  method: string;         // GET / POST / PATCH / DELETE
  url?: string | null;
  status?: number | null; // 200 / 404 / 0 (network error)
  error?: string | null;
}

export interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  pipelineRuns: PipelineRun[];
  sessionCost: number;
  createdAt: number;
  /** ms since epoch of the last activity on this session (send, receive,
   *  open). Drives the sidebar's "now / 12m / 3h / Tue" label and the
   *  ordering. Falls back to createdAt when the backend has no
   *  last_active_at yet. */
  updatedAt: number;
  messagesLoaded: boolean;
  memory: Record<string, unknown>;
  memoryRecalls: MemoryRecallItem[];
  // Live trace for the in-flight turn (cleared on next send).
  httpTrace: HttpTraceItem[];
  // A1 — accumulating in-flight assistant text from assistant_text_delta
  // events. Cleared on `done` (the canonical message lands in `messages`).
  // When non-empty AND `sending` is true, MessageList renders a
  // streaming bubble below the last finalised message.
  streamingAssistantText: string;
  // A2 — ordered accumulating list of runtime narration labels for the
  // in-flight turn. Each agent_intent OR a `status`/`thinking` text
  // push appends a string here (with dedup of consecutive identical
  // labels). MessageList renders the trail as faded sequential lines
  // beneath the last user message so the user sees the planner's
  // reasoning narrative ("inspecting Jobs" → "looking up jobs for
  // Bield" → "composing answer") instead of a single replacing pill.
  // Cleared on send + on done/error.
  intentTrail: string[];
  // ── Goal/Plan primitive (Phase C). `goal` drives <GoalBanner /> at
  // the top of the chat. `pendingDraftPlan` is a transient slot: the
  // backend fires `draft_action_plan` mid-turn; when `done` arrives we
  // attach it to the new assistant message via `message.draftPlan`
  // and clear this slot. Reset on session change. ──────────────────
  goal: GoalState;
  pendingDraftPlan: DraftActionPlan | null;
  // v2.2 task 9 — transient slot for the structural chain plan
  // emitted via `chain_plan_structured` (paired with `plan_committed`
  // by plan_id). Stamped onto the assistant message on `done`, same
  // pattern as `pendingDraftPlan`. step_* and chain_aborted/partial
  // events mutate this in-flight AND the message's chainPlan once
  // it's been stamped.
  pendingChainPlan: ChainPlanState | null;
  // Topic tracking Part 5. The most recent `topic_understanding`
  // payload — sticky, NOT a stream. `topicPathChainDepth` is pulled
  // from the most recent `topic_path_pinned` event so the Prewarm
  // row in DevPanel can render "E2 fired · 3-step chain hint loaded"
  // when a chain hint is loaded.
  topicUnderstanding: TopicUnderstanding | null;
  topicPathChainDepth: number | null;
  // v2 Part 6 — PROACTIVELY LOADED panel state. Per-turn list of
  // prewarm_action items, reset on prewarm_v2_start. When empty the
  // devbar renders the "— (no prefetches this turn)" empty state.
  proactivelyLoaded: PrewarmActionItem[];
  // v2 Part 6 — SESSION COST row state. Sums cost_usd across
  // llm_classifier + convogen_call + main-LLM usage events.
  costRollup: SessionCost;
}

let _msgId = 0;
const nextId = () => String(++_msgId);

/** Singularize a Simpro entity name: "jobs" → "job", "costCenters" → "cost center" */
function singularize(entity: string): string {
  // camelCase → spaced: "costCenters" → "cost Centers"
  const spaced = entity.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  // Simple plural strip
  if (spaced.endsWith("ies")) return spaced.slice(0, -3) + "y";
  if (spaced.endsWith("s") && !spaced.endsWith("ss"))
    return spaced.slice(0, -1);
  return spaced;
}

/** Derive a short user-facing description from write tool names */
function describeWriteTools(
  tools: { tool: string; input: Record<string, unknown> }[],
): string {
  const actions = tools.map((t) => {
    const parts = t.tool.split("_");
    const action = parts.pop() || ""; // "create", "delete", etc.
    const leaf = parts[parts.length - 1] || parts[0] || ""; // deepest entity
    const root = parts[0] || ""; // top-level entity
    const leafName = singularize(leaf);
    if (parts.length > 1 && root !== leaf) {
      return `${action} ${leafName} on ${singularize(root)}`;
    }
    return `${action} ${leafName}`;
  });
  if (actions.length === 1) return actions[0];
  return (
    actions.slice(0, 2).join(", ") +
    (actions.length > 2 ? ` +${actions.length - 2} more` : "")
  );
}

function titleFromMessages(msgs: ChatMessage[]): string {
  const first = msgs.find((m) => m.role === "user");
  if (!first) return "New Chat";
  const text = first.content.slice(0, 50);
  return text.length < first.content.length ? text + "…" : text;
}

export function useChatManager(
  onWorkflowSaved?: () => void,
  memoryEnabledRef?: React.RefObject<boolean>,
  userId?: string,
  tenantId?: string,
  onEmailToolResult?: (meta: { messageId: string; to: string; subject: string }) => void,
) {
  const freshId = useRef(crypto.randomUUID()).current;

  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeChatId, setActiveChatId] = useState<string>("");
  // `_sendingChatId` is the chatId of the in-flight turn (or null when
  // idle). The exposed `sending` derives from it as
  // `_sendingChatId === activeChatId` so switching to a different chat
  // mid-flight doesn't bleed the spinner / "recalling memories" status
  // into the other chat. Owen 2026-05-22 PM: "if I open a new chat and
  // say hello it does the recalling memories spinner, then if I open
  // another chat it shows the spinner on that one too — we need to
  // make sure it sticks to chat only and they're independent."
  const [_sendingChatId, setSendingChatId] = useState<string | null>(null);
  const sending = _sendingChatId !== null && _sendingChatId === activeChatId;
  const [statusText, setStatusText] = useState("");
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(
    null,
  );
  const [chatsLoaded, setChatsLoaded] = useState(false);

  const runIdRef = useRef(0);
  const saveFlagRef = useRef(false);
  const sessionIdRef = useRef<string | undefined>(undefined);
  const { consume } = useSSE<SSEEvent>();

  // ── Load saved chats on mount ──────────────────
  useEffect(() => {
    listSavedChats()
      .then((saved) => {
        const now = Date.now();
        const blankChat: ChatSession = {
          id: freshId,
          title: "New Chat",
          messages: [],
          pipelineRuns: [],
          sessionCost: 0,
          createdAt: now,
          updatedAt: now,
          messagesLoaded: true,
          memory: {},
          memoryRecalls: [],
          httpTrace: [],
          streamingAssistantText: "",
          intentTrail: [],
          goal: EMPTY_GOAL_STATE,
          pendingDraftPlan: null,
          pendingChainPlan: null,
          topicUnderstanding: null,
          topicPathChainDepth: null,
          proactivelyLoaded: [],
          costRollup: { thisTurnUsd: 0, totalUsd: 0, turnCount: 0 },
        };
        if (saved.length > 0) {
          const restored: ChatSession[] = saved.map((c) => {
            const created = new Date(c.created_at).getTime();
            const updated = c.updated_at
              ? new Date(c.updated_at).getTime()
              : created;
            return {
              id: c.chat_id,
              title: c.title,
              messages: [],
              pipelineRuns: [],
              sessionCost: 0,
              createdAt: created,
              updatedAt: updated,
              messagesLoaded: false,
              memory: {},
              memoryRecalls: [],
              httpTrace: [],
              streamingAssistantText: "",
              intentTrail: [],
              goal: EMPTY_GOAL_STATE,
              pendingDraftPlan: null,
              pendingChainPlan: null,
              topicUnderstanding: null,
              topicPathChainDepth: null,
              proactivelyLoaded: [],
              costRollup: { thisTurnUsd: 0, totalUsd: 0, turnCount: 0 },
            };
          });
          setSessions([blankChat, ...restored]);
          // Start on a fresh blank chat — don't auto-select old chats
          setActiveChatId(blankChat.id);
        } else {
          setSessions([blankChat]);
          setActiveChatId(blankChat.id);
        }
        setChatsLoaded(true);
      })
      .catch(() => {
        const now = Date.now();
        const blankChat: ChatSession = {
          id: freshId,
          title: "New Chat",
          messages: [],
          pipelineRuns: [],
          sessionCost: 0,
          createdAt: now,
          updatedAt: now,
          messagesLoaded: true,
          memory: {},
          memoryRecalls: [],
          httpTrace: [],
          streamingAssistantText: "",
          intentTrail: [],
          goal: EMPTY_GOAL_STATE,
          pendingDraftPlan: null,
          pendingChainPlan: null,
          topicUnderstanding: null,
          topicPathChainDepth: null,
          proactivelyLoaded: [],
          costRollup: { thisTurnUsd: 0, totalUsd: 0, turnCount: 0 },
        };
        setSessions([blankChat]);
        setActiveChatId(blankChat.id);
        setChatsLoaded(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── v2.2 task 9 — fixture inject for ChainPlanDiagram solo testing ──
  // ?fixture=chain-canonical | chain-failed injects a synthetic plan
  // onto a fresh assistant message. DEV-only — production builds short-
  // circuit because import.meta.env.DEV resolves to false at build time
  // and gets dead-code-eliminated by Vite.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!chatsLoaded) return;
    const params = new URLSearchParams(window.location.search);
    const fixture = params.get("fixture");
    if (
      fixture !== "chain-canonical" &&
      fixture !== "chain-failed" &&
      fixture !== "chain-long"
    )
      return;
    // Lazy import keeps the fixture out of the production bundle even
    // if the tree-shake misses (DEV guard above is the primary).
    import("@/components/Chat/_chainPlanFixtures").then((mod) => {
      const plan = mod.getChainPlanFixture(fixture);
      updateActive((s) => ({
        ...s,
        messages: [
          ...s.messages,
          {
            id: nextId(),
            role: "assistant" as const,
            content:
              fixture === "chain-failed"
                ? "Here's where the chain landed. Step 2 failed on validation; step 3 was skipped because the site ID never came back."
                : "Here's the plan — three records, in order. Each one's ID feeds the next. Pick a customer and I'll run it.",
            chainPlan: plan,
          },
        ],
      }));
    });
    // Only fire once when chats first load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatsLoaded]);

  // ── Lazy-load messages when activeChatId is set to an unloaded session ──
  useEffect(() => {
    if (!activeChatId) return;
    const session = sessions.find((s) => s.id === activeChatId);
    if (!session || session.messagesLoaded) return;
    getSavedChat(activeChatId)
      .then((saved) => {
        const loadedMessages = saved.messages.map((m) => ({
          id: nextId(),
          role: m.role as ChatMessage["role"],
          content: m.content,
        }));
        const loadedMemory = saved.memory || {};
        setSessions((p) =>
          p.map((s) =>
            s.id === activeChatId
              ? {
                  ...s,
                  title: saved.title || s.title,
                  messages: loadedMessages,
                  memory: loadedMemory,
                  messagesLoaded: true,
                }
              : s,
          ),
        );
        // Restore backend conversation state with loaded messages
        if (loadedMessages.length > 0) {
          const plainMsgs = loadedMessages.map((m) => ({
            role: m.role,
            content: m.content,
          }));
          apiRestoreChat(plainMsgs, loadedMemory).catch(() => {});
        } else {
          apiClearChat().catch(() => {});
        }
      })
      .catch(() => {
        setSessions((p) =>
          p.map((s) =>
            s.id === activeChatId ? { ...s, messagesLoaded: true } : s,
          ),
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChatId]);

  // ── Auto-save when flagged dirty ───────────────
  useEffect(() => {
    if (!saveFlagRef.current) return;
    saveFlagRef.current = false;
    const session = sessions.find((s) => s.id === activeChatId);
    if (!session || session.messages.length === 0) return;
    // Save using the backend memory session_id if available — that's the
    // actual DB row.  Fall back to the frontend chatId for new sessions.
    const backendId = (session.memory?.session_id as string) || session.id;
    const msgs = session.messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));
    apiSaveChat(backendId, session.title, msgs, session.memory).catch(() => {});
  }, [sessions, activeChatId]);

  const markDirty = useCallback(() => {
    saveFlagRef.current = true;
  }, []);

  // Helper: update the active session
  const updateActive = useCallback(
    (updater: (s: ChatSession) => ChatSession) => {
      setSessions((prev) =>
        prev.map((s) => (s.id === activeChatId ? updater(s) : s)),
      );
    },
    [activeChatId],
  );

  // Push a pipeline node to the active session's last run.
  // Stamps endedAt on the run whenever a `done` node lands, so the devbar
  // can show total wall-clock latency from send → final response.
  const pushNode = useCallback(
    (node: PipelineNode) => {
      updateActive((s) => {
        const runs = [...s.pipelineRuns];
        const prev = runs[runs.length - 1];
        const last = {
          ...prev,
          nodes: [...prev.nodes, node],
          endedAt: node.kind === "done" ? Date.now() : prev.endedAt,
        };
        runs[runs.length - 1] = last;
        return { ...s, pipelineRuns: runs };
      });
    },
    [updateActive],
  );

  // Update the last confirm node's resolved status
  const resolveConfirmNode = useCallback(
    (resolution: "approved" | "rejected") => {
      updateActive((s) => {
        const runs = [...s.pipelineRuns];
        const lastRun = { ...runs[runs.length - 1] };
        const nodes = [...lastRun.nodes];
        for (let i = nodes.length - 1; i >= 0; i--) {
          if (nodes[i].kind === "confirm") {
            nodes[i] = { ...nodes[i], resolved: resolution } as PipelineNode;
            break;
          }
        }
        lastRun.nodes = nodes;
        runs[runs.length - 1] = lastRun;
        return { ...s, pipelineRuns: runs };
      });
    },
    [updateActive],
  );

  // Get active session
  const activeSession =
    sessions.find((s) => s.id === activeChatId) || sessions[0] || null;

  // Keep sessionIdRef in sync so the send callback can access it without extra deps
  sessionIdRef.current =
    (activeSession?.memory?.session_id as string) || undefined;

  // ── Approve / reject write confirmation ─────────
  const approveWrite = useCallback(async () => {
    if (!pendingConfirm) return;
    await apiConfirmWrite(pendingConfirm.confirmId, true);
    resolveConfirmNode("approved");
    const desc = describeWriteTools(pendingConfirm.tools);
    updateActive((s) => ({
      ...s,
      messages: [
        ...s.messages,
        {
          id: nextId(),
          role: "system" as const,
          content: `Approved — ${desc}`,
        },
      ],
    }));
    setPendingConfirm(null);
    setStatusText("Approved — executing…");
    markDirty();
  }, [pendingConfirm, resolveConfirmNode, updateActive, markDirty]);

  const rejectWrite = useCallback(async () => {
    if (!pendingConfirm) return;
    await apiConfirmWrite(pendingConfirm.confirmId, false);
    resolveConfirmNode("rejected");
    const desc = describeWriteTools(pendingConfirm.tools);
    updateActive((s) => ({
      ...s,
      messages: [
        ...s.messages,
        {
          id: nextId(),
          role: "system" as const,
          content: `Rejected — ${desc}`,
        },
      ],
    }));
    setPendingConfirm(null);
    setStatusText("Write operations rejected.");
    markDirty();
  }, [pendingConfirm, resolveConfirmNode, updateActive, markDirty]);

  // ── Inject a message from outside (e.g. proactive notifications) ──
  const injectMessage = useCallback(
    (role: "user" | "assistant" | "system", content: string) => {
      updateActive((s) => ({
        ...s,
        messages: [...s.messages, { id: nextId(), role, content }],
      }));
      markDirty();
    },
    [updateActive, markDirty],
  );

  // ── Send message ────────────────────────────────
  const send = useCallback(
    async (text: string, fileRef?: string) => {
      if (!text.trim() || sending) return;
      // Track which chat is in-flight so the spinner stays on this
      // chat even if the user clicks over to another one. Captured
      // here at call-time, NOT read live, so a mid-send chat-switch
      // doesn't mis-route the indicator.
      const _chatIdForSend = activeChatId;
      setSendingChatId(_chatIdForSend);
      setStatusText("Routing…");

      const userMsg: ChatMessage = {
        id: nextId(),
        role: "user",
        content: text,
      };

      // Add user message and start pipeline run. Reset httpTrace so the
      // strip shows only this turn's CRM calls.
      const rid = ++runIdRef.current;
      updateActive((s) => ({
        ...s,
        messages: [...s.messages, userMsg],
        title:
          s.messages.length === 0
            ? text.slice(0, 50) + (text.length > 50 ? "…" : "")
            : s.title,
        updatedAt: Date.now(),
        pipelineRuns: [
          ...s.pipelineRuns,
          { id: rid, nodes: [{ kind: "active", text: "Routing…" }], startedAt: Date.now() },
        ],
        httpTrace: [],
        streamingAssistantText: "",
        intentTrail: [],
        // v2 Part 6 — new turn boundary on USER SEND (NOT on the
        // user_message SSE event, which arrives after classifier +
        // prewarm and would wipe out the values we just accumulated).
        // Defensive on restored sessions where costRollup may be
        // undefined.
        proactivelyLoaded: [],
        costRollup: {
          thisTurnUsd: 0,
          totalUsd: s.costRollup?.totalUsd ?? 0,
          turnCount: (s.costRollup?.turnCount ?? 0) + 1,
        },
      }));

      const toolNames: string[] = [];

      try {
        const resp = await chatStream(
          text,
          memoryEnabledRef?.current ?? false,
          fileRef,
          sessionIdRef.current,
          userId,
          tenantId,
        );
        await consume(resp, (event) => {
          switch (event.type) {
            case "status":
              setStatusText(event.text);
              break;
            case "intent":
              pushNode({
                kind: "intent",
                intents: event.intents,
                entities: event.entities,
                display: event.display,
                pruned: event.pruned,
              });
              break;
            case "router":
              pushNode({
                kind: "router",
                groups: event.groups,
                toolsLoaded: event.tools_loaded,
                totalTools: event.total_tools,
                cost: event.cost,
                enrichedQuery: event.enriched_query,
                backrefs: event.backrefs,
                carried: event.carried,
                autoChain: event.auto_chain,
              });
              break;
            case "thinking":
              if (event.text) {
                pushNode({ kind: "planner", text: event.text });
                setStatusText(event.text);
                // Also push to the intentTrail so the user sees the
                // LLM's intra-turn narration as faded chat lines (paired
                // with agent_intent runtime labels). Dedup consecutive
                // identical entries. Trim long thinking text to a
                // single-line preview — full prose stays in the devbar.
                const preview = event.text.length > 140
                  ? event.text.slice(0, 137) + "…"
                  : event.text;
                updateActive((s) => {
                  const last = s.intentTrail[s.intentTrail.length - 1];
                  if (last === preview) return s;
                  return { ...s, intentTrail: [...s.intentTrail, preview] };
                });
              } else {
                pushNode({ kind: "thinking", durationMs: event.duration_ms });
              }
              break;
            case "memory":
              pushNode({ kind: "memory", memory: event.memory });
              break;
            case "system_prompt":
              // Store system prompt on the current pipeline run
              updateActive((s) => {
                const runs = [...s.pipelineRuns];
                const last = { ...runs[runs.length - 1], systemPrompt: event.text };
                runs[runs.length - 1] = last;
                return { ...s, pipelineRuns: runs };
              });
              break;
            case "prewarm": {
              // Dedupe consecutive identical prewarm emits. The new
              // planner emits prewarm twice per turn (initial scan +
              // a second pass after entity_nouns resolves) — both
              // payloads can be byte-identical, so showing two panels
              // wastes vertical space. Hash by mention+candidate-ids;
              // if the previous node in this run is an identical
              // prewarm, drop the new one silently. Documented as
              // state.md known-issue #1 ("Duplicate prewarm emit");
              // a future fix in planner-core will suppress the
              // duplicate at source.
              const hints = event.hints ?? [];
              const fingerprint = JSON.stringify(
                hints.map((h) => [
                  h.mention,
                  (h.candidates ?? []).map((c) =>
                    `${c.entity}:${c.id}:${c.tier ?? ""}`).join("|"),
                ]),
              );
              const dup = activeSession?.pipelineRuns
                .at(-1)
                ?.nodes.at(-1);
              if (
                dup &&
                dup.kind === "prewarm" &&
                JSON.stringify(
                  (dup.hints ?? []).map((h) => [
                    h.mention,
                    (h.candidates ?? []).map((c) =>
                      `${c.entity}:${c.id}:${c.tier ?? ""}`).join("|"),
                  ]),
                ) === fingerprint
              ) {
                break;
              }
              pushNode({
                kind: "prewarm",
                summary: event.summary,
                hints,
              });
              break;
            }
            case "prewarm_filter":
              // F12 small-LLM noise filter decision log. status="applied"
              // means the filter actually narrowed the candidate set;
              // skipped_low_confidence / skipped_no_key / skipped_no_hits
              // are no-op outcomes that still surface for diagnostics.
              pushNode({
                kind: "prewarm_filter",
                status: event.status,
                kept: event.kept,
                dropped: event.dropped,
                confidence: event.confidence,
              });
              break;
            case "memory_filter":
              // F13 — planner-side filter on memory similarity_recall
              // bucket. always/scope bypass; recall_in→recall_kept
              // shows what the small LLM kept; outboundDropped is the
              // deterministic-drop bucket (prior assistant CRM responses
              // that would have leaked stale numbers).
              pushNode({
                kind: "memory_filter",
                status: event.status,
                confidence: event.confidence,
                alwaysCount: event.always_count,
                scopeCount: event.scope_count,
                recallIn: event.recall_in,
                recallKept: event.recall_kept,
                outboundDropped: event.outbound_dropped_count,
                durationMs: event.duration_ms,
              });
              break;
            case "tier_hit":
              // Tier 5 (attribute) or Tier 6 (metric) hit. Render in
              // DevPanel as a deterministic-prewarm panel alongside
              // the legacy Tier-1/Tier-3 panels.
              pushNode({
                kind: "tier_hit",
                tier: event.tier,
                hits: event.hits as Record<string, Array<Record<string, unknown>>>,
              });
              break;
            case "llm_input":
              // Owen 2026-05-26 — full system prompt + messages array
              // snapshot per LLM iteration. Renders as a single
              // expandable DevPanel node so the dev can audit exactly
              // what the model sees before each call.
              pushNode({
                kind: "llm_input",
                iteration: (event.iteration as number) ?? 0,
                systemPrompt: (event.system_prompt as string) ?? "",
                messages: (event.messages as Array<Record<string, unknown>>) ?? [],
                toolNames: (event.tool_names as string[]) ?? [],
                systemPromptChars: (event.system_prompt_chars as number) ?? 0,
                messagesChars: (event.messages_chars as number) ?? 0,
                tsMs: event.ts_ms as number | undefined,
              });
              break;
            case "memory_recall":
              pushNode({
                kind: "memory_recall",
                memories: event.memories,
                fullContext: event.context_snippet,
                durationMs: event.duration_ms,
              });
              // Accumulate recalled memories into session state
              updateActive((s) => ({
                ...s,
                memoryRecalls: [...s.memoryRecalls, ...event.memories],
              }));
              break;
            case "pipeline_trace":
              pushNode({
                kind: "pipeline_trace",
                engine: event.engine,
                intent: event.intent,
                fql: event.fql,
                capability: event.capability,
                final_requests: event.final_requests,
              });
              break;
            case "tool_call":
              toolNames.push(event.tool);
              pushNode({
                kind: "tool",
                tool: event.tool,
                input: event.input,
                result: event.result,
                durationMs: event.duration_ms,
                isError:
                  typeof event.result === "string" &&
                  event.result.startsWith("Error"),
                declined: event.declined,
              });
              if (
                event.tool === "plan_workflow" ||
                event.tool === "save_automation"
              )
                onWorkflowSaved?.();
              if (
                (event.tool === "email_search" || event.tool === "email_read") &&
                onEmailToolResult
              ) {
                try {
                  const parsed = typeof event.result === "string" ? JSON.parse(event.result) : event.result;
                  if (parsed?.messages?.length) {
                    const m = parsed.messages[0];
                    onEmailToolResult({ messageId: m.id, to: m.from || m.from_name || "", subject: m.subject || "" });
                  } else if (parsed?.id) {
                    onEmailToolResult({ messageId: parsed.id, to: parsed.from || "", subject: parsed.subject || "" });
                  }
                } catch { /* ignore parse errors */ }
              }
              break;
            case "http_call":
              // Live CRM trace from the new planner — push as a row in the
              // chat's live-trace strip. Cleared on the next user send.
              updateActive((s) => ({
                ...s,
                httpTrace: [
                  ...s.httpTrace,
                  {
                    id: s.httpTrace.length + 1,
                    opId: event.op_id ?? null,
                    method: event.method || "GET",
                    url: event.url ?? null,
                    status: event.status ?? null,
                    error: event.error ?? null,
                  },
                ],
              }));
              break;
            case "confirm_write":
              setPendingConfirm({
                confirmId: event.confirm_id,
                tools: event.tools,
                readTools: event.read_tools,
                reasoning: event.reasoning,
              });
              setStatusText("Awaiting write approval…");
              pushNode({
                kind: "confirm",
                confirmId: event.confirm_id,
                tools: event.tools,
                readTools: event.read_tools,
              });
              break;
            case "export": {
              const a = document.createElement("a");
              a.href = event.url;
              a.download = event.filename || "export.xlsx";
              document.body.appendChild(a);
              a.click();
              a.remove();
              break;
            }
            case "done": {
              pushNode({
                kind: "done",
                usage: event.usage,
                toolCount: toolNames.length,
              });
              // Fall back to client-side estimate when backend returns 0 cost
              // (current dev/test backends don't always price tokens).
              const cost = effectiveCost(event.usage);
              // Capture updated memory (entity IDs) from the backend
              const updatedMemory = event.memory || {};
              updateActive((s) => {
                // C1 fix 2026-06-02 — when chain_plan_structured fired
                // earlier this turn, it pushed a STUB assistant message
                // with empty content + chainPlan attached so the
                // diagram could mount mid-turn. On `done`, find that
                // stub and update its content (with toolCalls +
                // draftPlan stamped on too) instead of creating a new
                // assistant message. Otherwise create new as normal.
                const stubIdx = (() => {
                  for (let i = s.messages.length - 1; i >= 0; i--) {
                    const m = s.messages[i];
                    if (
                      m.role === "assistant" &&
                      m.chainPlan &&
                      m.content === ""
                    ) {
                      return i;
                    }
                  }
                  return -1;
                })();
                let nextMessages: ChatMessage[];
                if (stubIdx >= 0) {
                  nextMessages = s.messages.map((m, i) =>
                    i === stubIdx
                      ? {
                          ...m,
                          content: event.response,
                          toolCalls: [...toolNames],
                          draftPlan: s.pendingDraftPlan ?? undefined,
                        }
                      : m,
                  );
                } else {
                  // Phase C — if a `draft_action_plan` arrived earlier
                  // on this turn, stamp it onto the assistant message
                  // so MessageList renders <DraftActionPlanCard /> next
                  // to the text. Clear the transient slot.
                  const assistantMsg: ChatMessage = {
                    id: nextId(),
                    role: "assistant",
                    content: event.response,
                    toolCalls: [...toolNames],
                    draftPlan: s.pendingDraftPlan ?? undefined,
                    // Defensive — if pendingChainPlan was populated but
                    // chain_plan_structured never fired (unexpected),
                    // still stamp the plan onto the new message.
                    chainPlan: s.pendingChainPlan ?? undefined,
                  };
                  nextMessages = [...s.messages, assistantMsg];
                }
                // v2 Part 6 (2026-05-30 fix) — fold the main LLM cost
                // into costRollup too so the SESSION COST panel reflects
                // the WHOLE session cost (classifier + ConvoGen + main
                // LLM), not just classifier+convogen. Previously
                // costRollup tracked only the cheap calls and the
                // panel showed $0.0028 while the real session was
                // ~$0.013 — actively misleading.
                const priorRollup = s.costRollup ?? {
                  thisTurnUsd: 0, totalUsd: 0, turnCount: 0,
                };
                const nextRollup = {
                  thisTurnUsd: priorRollup.thisTurnUsd + cost,
                  totalUsd: priorRollup.totalUsd + cost,
                  turnCount: priorRollup.turnCount,
                };
                return {
                  ...s,
                  messages: nextMessages,
                  sessionCost: s.sessionCost + cost,
                  costRollup: nextRollup,
                  memory: { ...s.memory, ...updatedMemory },
                  // A1 — drop the in-flight streaming bubble; the
                  // canonical message is now in `messages`.
                  streamingAssistantText: "",
                  // A2 — clear the intent narration trail; the final
                  // answer subsumes "what I was doing" prose.
                  intentTrail: [],
                  pendingDraftPlan: null,
                  pendingChainPlan: null,
                  // v2 Part 6 (Owen + bug-diagnoser 2026-05-29 fix) — do NOT
                  // reset topicUnderstanding / topicPathChainDepth /
                  // proactivelyLoaded / costRollup here. These fields are
                  // sticky across the turn boundary by design: the CU block
                  // should show the LATEST classifier output, the
                  // PROACTIVELY LOADED panel should show the prefetches
                  // that happened on the turn that just completed, and
                  // SESSION COST must accumulate across the session.
                  // Per-turn resets are owned by send() (turn-start) and
                  // prewarm_v2_start (per-turn prefetch list). The bug
                  // that motivated this comment: this `done` handler was
                  // wiping all four fields the instant the turn finished,
                  // so the user never saw any v2 panel populated.
                };
              });
              markDirty();
              break;
            }
            // Fix #0 — DevPanel orchestration-trace user-prompt
            // rendering. The chat surface already shows the user's
            // bubble; this node makes the trace self-contained for
            // copy-paste diagnostics ("user said X → background did
            // Y → agent answered Z" without cross-referencing chat).
            case "user_message":
              pushNode({
                kind: "user_message",
                text: event.text,
                tsMs: event.ts_ms,
              });
              // v2 Part 6: turn boundary reset moved to the `send`
              // action (above) — user_message SSE arrives AFTER the
              // classifier + prewarm fire, so resetting state here
              // would wipe out the values we just accumulated.
              break;
            // ── New planner events (Q3/Q4/A1/A2) ──────────────────
            case "agent_intent":
              // A2 — runtime-derived label between LLM calls. Two
              // effects: (1) keep the live status pill in sync so the
              // 2-7s gap isn't silent, (2) ALSO append to intentTrail
              // so the user sees the cumulative narrative beneath the
              // user message (Owen 2026-05-22: "they need to see the
              // planner reassuring them like okay I'm checking this
              // just now okay listing them"). Dedup consecutive
              // identical labels so a noisy planner doesn't spam.
              if (event.label) {
                setStatusText(event.label);
                updateActive((s) => {
                  const last = s.intentTrail[s.intentTrail.length - 1];
                  if (last === event.label) return s;
                  return { ...s, intentTrail: [...s.intentTrail, event.label] };
                });
              }
              break;
            case "assistant_text_delta":
              // A1 — incremental tokens of the final answer. Accumulate
              // into a per-session buffer; MessageList renders it as a
              // streaming bubble. Replaced by the canonical text on
              // `done`.
              updateActive((s) => ({
                ...s,
                streamingAssistantText: s.streamingAssistantText + event.text,
              }));
              break;
            case "plan_committed":
              // Q4 — agent committed a multi-step chain. Render a plan
              // card with a checklist; step_* events mutate it in place.
              // v2.2 task 9 — ALSO seed pendingChainPlan with skeleton
              // step shells (op label + index). The structured body
              // lands in `chain_plan_structured` and gets merged in.
              updateActive((s) => {
                const runs = [...s.pipelineRuns];
                const prev = runs[runs.length - 1];
                const planNode: PipelineNode = {
                  kind: "plan",
                  planId: event.plan_id,
                  totalSteps: event.total_steps,
                  trigger: event.trigger,
                  steps: (event.steps || []).map((st) => ({
                    stepIdx: st.step_idx,
                    opId: st.op_id,
                    summary: st.summary,
                    opHumanLabel: st.op_human_label,
                    status: "pending" as const,
                  })),
                };
                runs[runs.length - 1] = {
                  ...prev,
                  nodes: [...prev.nodes, planNode],
                };
                const pendingChainPlan: ChainPlanState = {
                  planId: event.plan_id,
                  submitted: false,
                  steps: (event.steps || []).map((st) => ({
                    stepIdx: st.step_idx,
                    opId: st.op_id,
                    opHumanLabel: st.op_human_label || st.op_id,
                    isNew: /^(create|replace)_/.test(st.op_id),
                    body: {},
                    fkInputs: [],
                    prereqQuestions: [],
                    status: "pending" as const,
                  })),
                };
                return { ...s, pipelineRuns: runs, pendingChainPlan };
              });
              break;
            case "chain_plan_structured": {
              // v2.2 task 7 — structural body lands here alongside the
              // existing plan_committed. Push a STUB assistant message
              // immediately so <ChainPlanDiagram /> mounts MID-TURN
              // (C1 fix 2026-06-02). The `done` handler later detects
              // this stub (by chainPlan + empty content + matching
              // plan_id) and updates its content instead of creating
              // a new assistant message. Body may still be empty
              // pre-task-8 — diagram falls back to placeholder rows.
              updateActive((s) => {
                const fkAdapter = (fk: {
                  field: string;
                  fk_entity: string | null;
                  allowed_values: Array<{ id: number; name: string }>;
                  required: boolean;
                  submitted_value: number | string | null;
                }) => ({
                  field: fk.field,
                  fk_entity: fk.fk_entity,
                  allowed_values: fk.allowed_values || [],
                  required: fk.required,
                  submitted_value: fk.submitted_value,
                });
                let merged: ChainPlanState;
                if (
                  s.pendingChainPlan &&
                  s.pendingChainPlan.planId === event.plan_id
                ) {
                  const byIdx = new Map(
                    event.steps.map((st) => [st.step_idx, st]),
                  );
                  const mergedSteps: ChainPlanStepState[] =
                    s.pendingChainPlan.steps.map((shell) => {
                      const st = byIdx.get(shell.stepIdx);
                      if (!st) return shell;
                      return {
                        ...shell,
                        opHumanLabel: st.op_human_label || shell.opHumanLabel,
                        isNew: st.is_new,
                        body: st.body || {},
                        fkInputs: (st.fk_inputs || []).map(fkAdapter),
                        prereqQuestions: st.prereq_questions || [],
                      };
                    });
                  merged = { ...s.pendingChainPlan, steps: mergedSteps };
                } else {
                  // chain_plan_structured arrived without a matching
                  // pending plan — seed from the structured event.
                  merged = {
                    planId: event.plan_id,
                    submitted: false,
                    steps: event.steps.map((st) => ({
                      stepIdx: st.step_idx,
                      opId: st.op_id,
                      opHumanLabel: st.op_human_label || st.op_id,
                      isNew: st.is_new,
                      body: st.body || {},
                      fkInputs: (st.fk_inputs || []).map(fkAdapter),
                      prereqQuestions: st.prereq_questions || [],
                      status: "pending" as const,
                    })),
                  };
                }
                const stubMessage: ChatMessage = {
                  id: nextId(),
                  role: "assistant",
                  content: "",
                  chainPlan: merged,
                };
                return {
                  ...s,
                  messages: [...s.messages, stubMessage],
                  // Pending is now owned by the message — no dual source.
                  pendingChainPlan: null,
                };
              });
              break;
            }
            case "step_started":
            case "step_completed":
            case "step_failed":
            case "step_skipped": {
              // Mutate the matching plan node's step entry in place.
              // Skip silently when no plan node is on the run (orphan
              // step events shouldn't happen but guard anyway).
              const statusMap = {
                step_started: "running",
                step_completed: "completed",
                step_failed: "failed",
                step_skipped: "skipped",
              } as const;
              const newStatus = statusMap[event.type];
              // v2.2 task 9 — mutate the matching plan node (devbar)
              // AND the user-facing chainPlan slot, which lives EITHER
              // on pendingChainPlan (pre-`done`) OR on the assistant
              // message (post-`done`). Map devbar status → diagram
              // status: completed → done.
              const diagramStatus =
                newStatus === "completed" ? "done" : newStatus;
              updateActive((s) => {
                const runs = [...s.pipelineRuns];
                const lastRun = { ...runs[runs.length - 1] };
                const nodes = [...lastRun.nodes];
                for (let i = nodes.length - 1; i >= 0; i--) {
                  const n = nodes[i];
                  if (n.kind === "plan" && n.planId === event.plan_id) {
                    const steps = n.steps.map((st) =>
                      st.stepIdx === event.step_idx
                        ? {
                            ...st,
                            status: newStatus,
                            resultSummary:
                              event.type === "step_completed"
                                ? event.result_summary
                                : st.resultSummary,
                            newHandleId:
                              event.type === "step_completed"
                                ? event.new_handle_id
                                : st.newHandleId,
                            newEntityId:
                              event.type === "step_completed"
                                ? event.new_entity_id
                                : st.newEntityId,
                            durationMs:
                              event.type === "step_completed"
                                ? event.duration_ms
                                : st.durationMs,
                            failureMessage:
                              event.type === "step_failed"
                                ? event.reasons?.[0]?.message
                                : st.failureMessage,
                          }
                        : st,
                    );
                    nodes[i] = { ...n, steps };
                    break;
                  }
                }
                lastRun.nodes = nodes;
                runs[runs.length - 1] = lastRun;
                const mutateChainPlan = (
                  cp: ChainPlanState,
                ): ChainPlanState => ({
                  ...cp,
                  steps: cp.steps.map((st) =>
                    st.stepIdx === event.step_idx
                      ? {
                          ...st,
                          status: diagramStatus,
                          failureText:
                            event.type === "step_failed"
                              ? event.reasons?.[0]?.message
                              : st.failureText,
                        }
                      : st,
                  ),
                });
                const messages =
                  s.messages.length === 0
                    ? s.messages
                    : s.messages.map((m) =>
                        m.chainPlan && m.chainPlan.planId === event.plan_id
                          ? { ...m, chainPlan: mutateChainPlan(m.chainPlan) }
                          : m,
                      );
                const pendingChainPlan =
                  s.pendingChainPlan &&
                  s.pendingChainPlan.planId === event.plan_id
                    ? mutateChainPlan(s.pendingChainPlan)
                    : s.pendingChainPlan;
                return {
                  ...s,
                  pipelineRuns: runs,
                  messages,
                  pendingChainPlan,
                };
              });
              if (event.type === "step_started") {
                setStatusText(
                  `step ${event.step_idx}/${event.total_steps}: ${event.summary || event.op_id}`,
                );
              }
              break;
            }
            case "chain_aborted":
              // User pressed Stop OR fatal mid-chain error. Push a
              // pipeline node for the devbar AND inject a system message
              // into the chat surface so the user sees acknowledgement.
              pushNode({
                kind: "chain_aborted",
                planId: event.plan_id,
                reason: event.reason,
                completedSteps: event.completed_steps,
                partialHandles: (event.partial_handles || []).map((h) => ({
                  step: h.step,
                  opId: h.op_id,
                  handleId: h.handle_id,
                  newId: h.new_id,
                })),
              });
              {
                const orphans = (event.partial_handles || []).length;
                const verb = event.reason === "user_stop" ? "stopped" : "aborted";
                const abortPlanId = event.plan_id;
                updateActive((s) => {
                  // v2.2 task 9 — mark the diagram as aborted so it
                  // locks Approve + surfaces the chain-level abort.
                  const markAborted = (
                    cp: ChainPlanState,
                  ): ChainPlanState => ({ ...cp, aborted: true, abortReason: event.reason });
                  const messages = [
                    ...s.messages.map((m) =>
                      m.chainPlan &&
                      abortPlanId &&
                      m.chainPlan.planId === abortPlanId
                        ? { ...m, chainPlan: markAborted(m.chainPlan) }
                        : m,
                    ),
                    {
                      id: nextId(),
                      role: "system" as const,
                      content: orphans
                        ? `Chain ${verb} — ${orphans} entit${orphans === 1 ? "y" : "ies"} created before stop. Check the devbar for IDs.`
                        : `Turn ${verb}.`,
                    },
                  ];
                  const pendingChainPlan =
                    s.pendingChainPlan &&
                    abortPlanId &&
                    s.pendingChainPlan.planId === abortPlanId
                      ? markAborted(s.pendingChainPlan)
                      : s.pendingChainPlan;
                  return {
                    ...s,
                    messages,
                    pendingChainPlan,
                    streamingAssistantText: "",
                    intentTrail: [],
                  };
                });
              }
              break;
            case "chain_partial":
              // Distinct from chain_aborted — fatal mid-chain with
              // orphan handles. Reuse the chain_aborted node shape so
              // the devbar renders one component for both classes.
              pushNode({
                kind: "chain_aborted",
                planId: event.plan_id ?? null,
                reason: `chain_partial_at_step_${event.failed_at_step ?? "?"}`,
                completedSteps: event.completed_steps ?? 0,
                partialHandles: (event.partial_handles || []).map((h) => ({
                  step: h.step,
                  opId: h.op_id,
                  handleId: h.handle_id,
                  newId: h.new_id,
                })),
              });
              {
                // v2.2 task 9 — also mark the diagram aborted.
                const partialPlanId = event.plan_id;
                if (partialPlanId) {
                  updateActive((s) => {
                    const markAborted = (
                      cp: ChainPlanState,
                    ): ChainPlanState => ({
                      ...cp,
                      aborted: true,
                      abortReason: `partial_at_step_${event.failed_at_step ?? "?"}`,
                    });
                    return {
                      ...s,
                      messages: s.messages.map((m) =>
                        m.chainPlan && m.chainPlan.planId === partialPlanId
                          ? { ...m, chainPlan: markAborted(m.chainPlan) }
                          : m,
                      ),
                      pendingChainPlan:
                        s.pendingChainPlan &&
                        s.pendingChainPlan.planId === partialPlanId
                          ? markAborted(s.pendingChainPlan)
                          : s.pendingChainPlan,
                    };
                  });
                }
              }
              break;
            case "plan_missing":
              // Q3 v1 lenient — soft warning. Surfaces in devbar; the
              // dispatch still runs.
              pushNode({
                kind: "plan_missing",
                trigger: event.trigger,
                firstOpId: event.first_op_id,
                warning: event.warning,
              });
              break;
            case "fact_saved":
            case "fact_pending_confirmation":
              // Memory Manager — explicit save fired by the LLM. Push
              // a devbar node; the LLM's own reply text narrates this
              // to the user in plain English.
              pushNode({
                kind: "fact_saved",
                pending: event.type === "fact_pending_confirmation",
                factId: event.fact_id,
                factType: event.fact_type,
                humanReadable: event.human_readable,
                scope: event.scope,
                status: event.status,
              });
              break;
            case "chain_paused":
            case "chain_resumed":
              // F10 territory — not wired in v1. Drop quietly.
              break;
            // ── Goal/Plan primitive (Phase C — planner 2026-05-26) ──
            // Five events drive the GoalBanner state machine. The
            // banner sits above MessageList and reflects the current
            // primary goal + any pending change proposal.
            case "goal_set":
              updateActive((s) => ({
                ...s,
                goal: {
                  primaryGoal: event.goal,
                  goalHistory: [
                    ...s.goal.goalHistory,
                    {
                      goal: event.goal,
                      setAt: new Date().toISOString(),
                      setVia: event.set_via,
                      prevGoal: null,
                    },
                  ],
                  pendingChange: null,
                },
              }));
              break;
            case "goal_revised":
              updateActive((s) => ({
                ...s,
                goal: {
                  primaryGoal: event.goal,
                  goalHistory: [
                    ...s.goal.goalHistory,
                    {
                      goal: event.goal,
                      setAt: new Date().toISOString(),
                      setVia: event.set_via,
                      prevGoal: event.prev_goal,
                    },
                  ],
                  pendingChange: null,
                },
              }));
              break;
            case "goal_change_proposed":
              updateActive((s) => ({
                ...s,
                goal: {
                  ...s.goal,
                  pendingChange: {
                    proposedGoal: event.proposed_goal,
                    currentGoal: event.current_goal,
                    reason: event.reason,
                    proposedAt: new Date().toISOString(),
                  },
                },
              }));
              break;
            case "goal_change_accepted":
              updateActive((s) => ({
                ...s,
                goal: {
                  primaryGoal: event.new_goal,
                  goalHistory: [
                    ...s.goal.goalHistory,
                    {
                      goal: event.new_goal,
                      setAt: new Date().toISOString(),
                      setVia: "agent_proposed_user_confirmed",
                      prevGoal: event.prev_goal,
                    },
                  ],
                  pendingChange: null,
                },
              }));
              break;
            case "goal_change_rejected":
              updateActive((s) => ({
                ...s,
                goal: { ...s.goal, pendingChange: null },
              }));
              break;
            // Draft action plan card — captured into pendingDraftPlan
            // until the matching `done` event lands, which copies it
            // onto the new assistant message and clears the slot.
            case "draft_action_plan":
              updateActive((s) => ({
                ...s,
                pendingDraftPlan: {
                  id: `dp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                  name: event.name,
                  description: event.description,
                  whenPlainEnglish: event.when_plain_english,
                  thenSteps: event.then_steps || [],
                  notificationsPlainEnglish: event.notifications_plain_english,
                  manualConfirmationRequired: event.manual_confirmation_required,
                },
              }));
              break;
            // Extend the existing chain-plan card with a "Plan for: …"
            // header once the planner links it to the active goal.
            case "plan_linked_to_goal":
              updateActive((s) => {
                const runs = s.pipelineRuns.map((run) => ({
                  ...run,
                  nodes: run.nodes.map((n) =>
                    n.kind === "plan" && n.planId === event.plan_id
                      ? { ...n, goal: event.goal }
                      : n,
                  ),
                }));
                return { ...s, pipelineRuns: runs };
              });
              break;
            case "plan_unlinked":
              updateActive((s) => {
                const runs = s.pipelineRuns.map((run) => ({
                  ...run,
                  nodes: run.nodes.map((n) =>
                    n.kind === "plan" && n.planId === event.plan_id
                      ? { ...n, outcome: event.outcome }
                      : n,
                  ),
                }));
                return { ...s, pipelineRuns: runs };
              });
              break;
            // ── Topic tracking Part 5 — Conversation Understander block ──
            // The planner emits `topic_understanding` every turn with the
            // current goal/topic/intent/pinned_path/prewarm_actions/
            // history_pair_count snapshot. Sticky — the last payload is
            // what the block renders, no append. `topic_path_pinned`
            // arrives separately and carries `has_chain` / chain_summary;
            // we pull chain depth out of it so the Prewarm row can show
            // "E2 fired · 3-step chain hint loaded".
            // `active_entity_changed` is logged-only at this stage — the
            // canonical state lives in topic_understanding; the change
            // event is for trace correlation.
            case "topic_understanding": {
              const evt = event as unknown as {
                goal: string | null;
                active_entity: TopicActiveEntity | null;
                topic_intent: TopicIntent | null;
                pinned_path: string | null;
                prewarm_actions?: string[];
                history_pair_count?: number;
                // PM Parts 3+ debug field — Owen 2026-05-31 unblock.
                entities?: unknown;
              };
              updateActive((s) => ({
                ...s,
                topicUnderstanding: {
                  goal: evt.goal ?? null,
                  active_entity: evt.active_entity ?? null,
                  topic_intent: evt.topic_intent ?? null,
                  pinned_path: evt.pinned_path ?? null,
                  prewarm_actions: Array.isArray(evt.prewarm_actions)
                    ? evt.prewarm_actions
                    : [],
                  history_pair_count: typeof evt.history_pair_count === "number"
                    ? evt.history_pair_count
                    : 0,
                },
              }));
              break;
            }
            case "topic_path_pinned": {
              // Read the chain depth so the Prewarm row can render
              // "E2 fired · 3-step chain hint loaded". The planner ships
              // it as `chain_summary.length` OR a `chain_steps` count —
              // accept either shape defensively.
              const evt = event as unknown as {
                chain_summary?: Array<unknown>;
                chain_steps?: number;
                has_chain?: boolean;
              };
              let depth: number | null = null;
              if (Array.isArray(evt.chain_summary)) {
                depth = evt.chain_summary.length;
              } else if (typeof evt.chain_steps === "number") {
                depth = evt.chain_steps;
              } else if (evt.has_chain === true) {
                depth = 0;
              }
              updateActive((s) => ({ ...s, topicPathChainDepth: depth }));
              break;
            }
            case "active_entity_changed":
              // No-op for now — the topic_understanding event carries
              // the new entity state, and that's what the block reads.
              // Kept as an explicit case so the switch is exhaustive +
              // future trace correlation can hook in here.
              break;
            case "entities_changed":
              // No-op — entities are carried on topic_understanding.
              break;
            // ── v2 Part 6 — PROACTIVELY LOADED panel + SESSION COST ──
            // prewarm_v2_start resets the per-turn list. Each
            // prewarm_action appends to it. prewarm_v2_done is purely
            // informational — no state mutation.
            case "prewarm_v2_start":
              updateActive((s) => ({ ...s, proactivelyLoaded: [] }));
              break;
            case "prewarm_v2_done":
              break;
            case "prewarm_action": {
              const evt = event as unknown as {
                action?: string;
                target?: string;
                duration_ms?: number;
                ok?: boolean;
                bytes?: number;
                cache_hit?: boolean;
                error?: string | null;
              };
              const item: PrewarmActionItem = {
                action: evt.action || "",
                target: evt.target || "",
                duration_ms: typeof evt.duration_ms === "number" ? evt.duration_ms : 0,
                ok: !!evt.ok,
                bytes: typeof evt.bytes === "number" ? evt.bytes : 0,
                cache_hit: !!evt.cache_hit,
                error: evt.error ?? null,
              };
              updateActive((s) => ({
                ...s,
                proactivelyLoaded: [...(s.proactivelyLoaded ?? []), item],
              }));
              break;
            }
            case "llm_classifier": {
              // v2 Part 6 — capture classifier cost_usd into the
              // SESSION COST row. Defensive on restored sessions:
              // costRollup may be undefined on chats saved before
              // Part 6 fields existed.
              const evt = event as unknown as {
                source?: string;
                model?: string;
                duration_ms?: number;
                message_class?: string;
                goal_signal?: string;
                topic_intent?: string | null;
                new_goal_label?: string | null;
                confidence?: string;
                entities?: Array<Record<string, unknown>>;
                to_resolve?: Array<Record<string, unknown>>;
                active_entity?: Record<string, unknown> | null;
                direct_reply_present?: boolean;
                input_tokens?: number;
                output_tokens?: number;
                cached_tokens?: number;
                cost_usd?: number;
                error?: string | null;
              };
              // v2 Part 6.b — push a visible trace node so the dev can
              // see what the classifier said per message.
              pushNode({
                kind: "classifier",
                source: evt.source ?? "unknown",
                model: evt.model,
                durationMs: evt.duration_ms,
                messageClass: evt.message_class,
                goalSignal: evt.goal_signal,
                topicIntent: evt.topic_intent ?? null,
                newGoalLabel: evt.new_goal_label ?? null,
                confidence: evt.confidence,
                entities: evt.entities ?? [],
                toResolve: evt.to_resolve ?? [],
                activeEntity: evt.active_entity ?? null,
                directReplyPresent: evt.direct_reply_present,
                inputTokens: evt.input_tokens,
                outputTokens: evt.output_tokens,
                cachedTokens: evt.cached_tokens,
                costUsd: evt.cost_usd,
                error: evt.error ?? null,
              });
              const c = evt.cost_usd ?? 0;
              if (c > 0) {
                updateActive((s) => {
                  const prior = s.costRollup ?? {
                    thisTurnUsd: 0, totalUsd: 0, turnCount: 0,
                  };
                  return {
                    ...s,
                    costRollup: {
                      thisTurnUsd: prior.thisTurnUsd + c,
                      totalUsd: prior.totalUsd + c,
                      turnCount: prior.turnCount,
                    },
                  };
                });
              }
              break;
            }
            case "convogen_call": {
              const c = (event as unknown as { cost_usd?: number })
                .cost_usd ?? 0;
              if (c > 0) {
                updateActive((s) => {
                  const prior = s.costRollup ?? {
                    thisTurnUsd: 0, totalUsd: 0, turnCount: 0,
                  };
                  return {
                    ...s,
                    costRollup: {
                      thisTurnUsd: prior.thisTurnUsd + c,
                      totalUsd: prior.totalUsd + c,
                      turnCount: prior.turnCount,
                    },
                  };
                });
              }
              break;
            }
            // ── Existing events continued ─────────────────────────
            case "error":
              pushNode({ kind: "done", toolCount: toolNames.length });
              updateActive((s) => ({
                ...s,
                messages: [
                  ...s.messages,
                  { id: nextId(), role: "assistant", content: event.response },
                ],
                streamingAssistantText: "",
          intentTrail: [],
              }));
              markDirty();
              break;
          }
        });
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        pushNode({ kind: "done", toolCount: 0 });
        updateActive((s) => ({
          ...s,
          messages: [
            ...s.messages,
            {
              id: nextId(),
              role: "assistant",
              content: `Connection error: ${msg}`,
            },
          ],
        }));
        markDirty();
      } finally {
        setSendingChatId(null);
        setStatusText("");
      }
    },
    [sending, consume, pushNode, updateActive, onWorkflowSaved, markDirty,
     activeChatId],
  );

  // ── New chat ────────────────────────────────────
  const newChat = useCallback(async () => {
    // If an empty "New Chat" tab already exists, just switch to it
    const existing = sessions.find(
      (s) => s.title === "New Chat" && s.messages.length === 0,
    );
    if (existing) {
      setActiveChatId(existing.id);
      await apiClearChat();
      return;
    }
    await apiClearChat();
    const session: ChatSession = {
      id: crypto.randomUUID(),
      title: "New Chat",
      messages: [],
      pipelineRuns: [],
      sessionCost: 0,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      messagesLoaded: true,
      memory: {},
      memoryRecalls: [],
      httpTrace: [],
      streamingAssistantText: "",
      intentTrail: [],
      goal: EMPTY_GOAL_STATE,
      pendingDraftPlan: null,
      pendingChainPlan: null,
      topicUnderstanding: null,
      topicPathChainDepth: null,
      proactivelyLoaded: [],
      costRollup: { thisTurnUsd: 0, totalUsd: 0, turnCount: 0 },
    };
    setSessions((prev) => [session, ...prev]);
    setActiveChatId(session.id);
  }, [sessions]);

  // ── Switch chat ─────────────────────────────────
  const switchChat = useCallback(
    async (chatId: string) => {
      setActiveChatId(chatId);
      // Lazy-load is handled by the activeChatId useEffect — no duplicate fetch here.
      // Restore backend state for already-loaded sessions.
      const session = sessions.find((s) => s.id === chatId);
      if (session?.messagesLoaded && session.messages.length > 0) {
        const plainMsgs = session.messages.map((m) => ({
          role: m.role,
          content: m.content,
        }));
        apiRestoreChat(plainMsgs, session.memory || {}).catch(() => {});
      } else if (session?.messagesLoaded && session.messages.length === 0) {
        // Blank session — clear backend
        apiClearChat().catch(() => {});
      }
      // If not loaded yet, the lazy-load effect will handle it.
    },
    [sessions],
  );

  // ── Clear current chat ──────────────────────────
  const clearCurrentChat = useCallback(async () => {
    await apiClearChat();
    updateActive((s) => ({
      ...s,
      messages: [],
      pipelineRuns: [],
      sessionCost: 0,
      title: "New Chat",
    }));
  }, [updateActive]);

  // ── Stop button — request in-flight turn to abort at next step boundary ──
  const cancel = useCallback(async () => {
    if (!sending) return;
    try {
      await apiCancelChat(sessionIdRef.current);
    } catch {
      /* fire-and-forget; the chain_aborted SSE event (if fired) will
         clean up state. Swallow errors so a network blip doesn't
         leave the UI stuck. */
    }
  }, [sending]);

  // ── End session (triggers memory extraction) ────
  const endSessionChat = useCallback(
    async (chatId: string) => {
      // Find the backend memory session_id (different from frontend chatId)
      const session = sessions.find((s) => s.id === chatId);
      const memorySessionId = session?.memory?.session_id as string | undefined;
      const idToEnd = memorySessionId || chatId;
      try {
        await apiEndSession(idToEnd);
        console.log(`[memory] Session ended: ${idToEnd}`);
      } catch (err) {
        console.warn("[memory] End session failed:", err);
      }
    },
    [sessions],
  );

  // ── Rename a chat session ───────────────────────
  const renameChat = useCallback(
    async (chatId: string, title: string) => {
      const clean = title.trim();
      if (!clean) return;
      // Optimistic local update — server PUT is fire-and-forget; if the
      // backend errors we silently keep the local rename (next page refresh
      // would resync from the server).
      setSessions((prev) =>
        prev.map((s) => (s.id === chatId ? { ...s, title: clean } : s)),
      );
      apiSaveChat(chatId, clean, [], {}).catch(() => {});
    },
    [],
  );

  // ── Delete a chat session ───────────────────────
  const deleteChat = useCallback(
    async (chatId: string) => {
      // Delete from backend
      deleteSavedChat(chatId).catch(() => {});

      const isDeletingActive = chatId === activeChatId;

      setSessions((prev) => {
        const filtered = prev.filter((s) => s.id !== chatId);
        if (filtered.length === 0) {
          // No sessions left — create a blank one
          const fresh: ChatSession = {
            id: crypto.randomUUID(),
            title: "New Chat",
            messages: [],
            pipelineRuns: [],
            sessionCost: 0,
            createdAt: Date.now(),
            updatedAt: Date.now(),
            messagesLoaded: true,
            memory: {},
            memoryRecalls: [],
            httpTrace: [],
            streamingAssistantText: "",
            intentTrail: [],
            goal: EMPTY_GOAL_STATE,
            pendingDraftPlan: null,
            pendingChainPlan: null,
            topicUnderstanding: null,
            topicPathChainDepth: null,
            proactivelyLoaded: [],
            costRollup: { thisTurnUsd: 0, totalUsd: 0, turnCount: 0 },
          };
          if (isDeletingActive) setActiveChatId(fresh.id);
          return [fresh];
        }
        // If we just deleted the active chat, switch to the first remaining
        if (isDeletingActive) setActiveChatId(filtered[0].id);
        return filtered;
      });

      if (isDeletingActive) {
        await apiClearChat();
      }
    },
    [activeChatId],
  );

  // ── Approve a committed chain plan (v2.2 task 9) ─────────────
  // POSTs the structured approve to /api/chat/stream. The runtime
  // pre-classifier (task 10) will detect text === "approve_chain"
  // and dispatch the chain with the prereq answers threaded in,
  // bypassing the LLM. Until task 10 lands the classifier treats
  // this as a conversational message — expected dev behaviour;
  // the 60s no-event watchdog inside the diagram catches that case.
  // We consume the response stream so the connection drains and
  // step_*/chain_* events (when task 10 is live) flow through the
  // same status mutation logic as the normal send pathway.
  const approveChain = useCallback(
    async (planId: string, prereqAnswers: Record<string, unknown>) => {
      updateActive((s) => {
        const lockSubmitted = (cp: ChainPlanState): ChainPlanState =>
          cp.planId === planId ? { ...cp, submitted: true } : cp;
        return {
          ...s,
          messages: s.messages.map((m) =>
            m.chainPlan && m.chainPlan.planId === planId
              ? { ...m, chainPlan: lockSubmitted(m.chainPlan) }
              : m,
          ),
          pendingChainPlan:
            s.pendingChainPlan && s.pendingChainPlan.planId === planId
              ? lockSubmitted(s.pendingChainPlan)
              : s.pendingChainPlan,
        };
      });

      try {
        const resp = await approveChainStream(
          planId,
          prereqAnswers,
          sessionIdRef.current,
        );
        // Drain the response. We only act on lifecycle events here
        // — the giant inline send-consume handler isn't reachable
        // from this scope. Once task 10 lands the step_* events
        // flowing here will mutate the diagram via the same
        // mutateChainPlan logic as the normal turn.
        await consume(resp, (event) => {
          if (
            event.type === "step_started" ||
            event.type === "step_completed" ||
            event.type === "step_failed" ||
            event.type === "step_skipped"
          ) {
            const statusMap = {
              step_started: "running",
              step_completed: "done",
              step_failed: "failed",
              step_skipped: "skipped",
            } as const;
            const diagramStatus = statusMap[event.type];
            updateActive((s) => {
              const mutate = (cp: ChainPlanState): ChainPlanState => ({
                ...cp,
                steps: cp.steps.map((st) =>
                  st.stepIdx === event.step_idx
                    ? {
                        ...st,
                        status: diagramStatus,
                        failureText:
                          event.type === "step_failed"
                            ? event.reasons?.[0]?.message
                            : st.failureText,
                      }
                    : st,
                ),
              });
              return {
                ...s,
                messages: s.messages.map((m) =>
                  m.chainPlan && m.chainPlan.planId === event.plan_id
                    ? { ...m, chainPlan: mutate(m.chainPlan) }
                    : m,
                ),
              };
            });
          } else if (
            event.type === "chain_aborted" ||
            event.type === "chain_partial"
          ) {
            const reason =
              event.type === "chain_aborted"
                ? event.reason
                : `partial_at_step_${
                    (event as { failed_at_step?: number }).failed_at_step ?? "?"
                  }`;
            const targetPlanId = event.plan_id;
            if (!targetPlanId) return;
            updateActive((s) => ({
              ...s,
              messages: s.messages.map((m) =>
                m.chainPlan && m.chainPlan.planId === targetPlanId
                  ? {
                      ...m,
                      chainPlan: {
                        ...m.chainPlan,
                        aborted: true,
                        abortReason: reason,
                      },
                    }
                  : m,
              ),
            }));
          }
        });
      } catch {
        // Network failure surfaces via the 60s watchdog inside the
        // diagram. No toast — Approve button reverts to its enabled
        // state via the chainPlan.submitted reset below.
        updateActive((s) => ({
          ...s,
          messages: s.messages.map((m) =>
            m.chainPlan && m.chainPlan.planId === planId
              ? {
                  ...m,
                  chainPlan: { ...m.chainPlan, submitted: false },
                }
              : m,
          ),
        }));
      }
    },
    [updateActive, consume],
  );

  return {
    sessions,
    activeChatId,
    activeSession,
    sending,
    statusText,
    pendingConfirm,
    chatsLoaded,
    send,
    cancel,
    injectMessage,
    approveWrite,
    rejectWrite,
    approveChain,
    newChat,
    switchChat,
    clearCurrentChat,
    deleteChat,
    renameChat,
    endSessionChat,
  };
}

export { titleFromMessages };
